import React, { useEffect, useState } from "react";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai } from "../utils/Utils";
import "./AgeGen.css";
import { useTranslation } from "react-i18next";

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const AgeGenSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  const placeholderRows = [1, 2, 3];

  return (
    <div className="agegen-wrapper">
      <div className="agegen-card">
        {/* SectionHeader skeleton */}
        <div style={{ padding: "12px 16px 0 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {/* Icon */}
            <div
              style={{
                ...shimmerBaseStyle,
                width: 32,
                height: 32,
                borderRadius: 999,
              }}
            />

            {/* Title + subtitle */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  ...shimmerBaseStyle,
                  height: 18,
                  width: "60%",
                  borderRadius: 8,
                }}
              />
              <div
                style={{
                  ...shimmerBaseStyle,
                  height: 14,
                  width: "40%",
                  borderRadius: 8,
                }}
              />
            </div>

            {/* Info icon */}
            <div
              style={{
                ...shimmerBaseStyle,
                width: 20,
                height: 20,
                borderRadius: 999,
              }}
            />
          </div>
        </div>

        {/* Table skeleton */}
        <div className="agegen-table" style={{ marginTop: 12 }}>
          {/* Header row */}
          <div className="agegen-header-row">
            <div className="agegen-col agegen-col-age">
              <div
                style={{
                  ...shimmerBaseStyle,
                  height: 16,
                  width: 80,
                  borderRadius: 8,
                }}
              />
            </div>
            <div className="agegen-col agegen-col-label">
              <span className="agegen-pill agegen-pill-desktop">
                <span
                  style={{
                    ...shimmerBaseStyle,
                    display: "inline-block",
                    height: 20,
                    minWidth: 100,
                    borderRadius: 999,
                  }}
                />
              </span>
            </div>
            <div className="agegen-col agegen-col-label">
              <span className="agegen-pill agegen-pill-desktop">
                <span
                  style={{
                    ...shimmerBaseStyle,
                    display: "inline-block",
                    height: 20,
                    minWidth: 100,
                    borderRadius: 999,
                  }}
                />
              </span>
            </div>
          </div>

          {/* Data rows */}
          {placeholderRows.map((i, idx) => (
            <React.Fragment key={i}>
              {idx > 0 && <div className="agegen-divider" />}

              <div className="agegen-row">
                {/* Age col */}
                <div className="agegen-col agegen-col-age">
                  <div className="agegen-age-text">
                    <div
                      style={{
                        ...shimmerBaseStyle,
                        height: 18,
                        width: 90,
                        borderRadius: 8,
                        marginBottom: 6,
                      }}
                    />
                    <div
                      style={{
                        ...shimmerBaseStyle,
                        height: 14,
                        width: 70,
                        borderRadius: 8,
                      }}
                    />
                  </div>
                </div>

                {/* Without GOQii image col */}
                <div className="agegen-col agegen-col-image">
                  <div className="agegen-pill agegen-pill-mobile">
                    <span
                      style={{
                        ...shimmerBaseStyle,
                        display: "inline-block",
                        height: 20,
                        minWidth: 90,
                        borderRadius: 999,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      ...shimmerBaseStyle,
                      width: 96,
                      height: 96,
                      borderRadius: 16,
                      marginTop: 4,
                    }}
                  />
                </div>

                {/* With GOQii image col */}
                <div className="agegen-col agegen-col-image">
                  <div className="agegen-pill agegen-pill-mobile">
                    <span
                      style={{
                        ...shimmerBaseStyle,
                        display: "inline-block",
                        height: 20,
                        minWidth: 90,
                        borderRadius: 999,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      ...shimmerBaseStyle,
                      width: 96,
                      height: 96,
                      borderRadius: 16,
                      marginTop: 4,
                    }}
                  />
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const AgeGen = ({ onHide } = {}) => {
  const { t } = useTranslation();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) {
      setLoading(false);
      return;
    }

    lifestyleApi
      .agegenData(encryptedId)
      .then((res) => {
        if (res?.data?.code === 200) {
          setApiData(res.data.data);
        }
      })
      .catch((err) => console.error("AgeGen error:", err))
      .finally(() => setLoading(false));
  }, []);

  const img = apiData?.img || {};
  const noAiImage = !!apiData && img.aiImageExists === false;

  useEffect(() => {
    if (noAiImage && onHide) onHide();
  }, [noAiImage]);

  if (loading) {
    return <AgeGenSkeleton />;
  }

  if (!apiData) {
    return (
      <div className="agegen-wrapper">
        <div className="agegen-loading">No data available.</div>
      </div>
    );
  }

  const ageProfile = apiData.age_profile || {};

  if (noAiImage) {
    if (onHide) return null;

    return (
      <div className="agegen-wrapper">
        <div className="agegen-loading">
          Time travel not available for this user yet.
        </div>
      </div>
    );
  }

  const rows = [
    {
      age: 50,
      year: ageProfile.fifty_year,
      withoutImg: img.OldImg1,
      withImg: img.goqiiOldImg1,
    },
    {
      age: 60,
      year: ageProfile.sixty_year,
      withoutImg: img.OldImg2,
      withImg: img.goqiiOldImg2,
    },
    {
      age: 70,
      year: ageProfile.seventy_year,
      withoutImg: img.OldImg3,
      withImg: img.goqiiOldImg3,
    },
  ].filter((r) => r.year && (r.withImg || r.withoutImg));

  if (!rows.length) {
    return (
      <div className="agegen-wrapper">
        <div className="agegen-loading">No future time travel data found.</div>
      </div>
    );
  }

  return (
    <div className="agegen-wrapper">
      <div className="agegen-card">
        <div className="agegen-header">
          <SectionHeader
          icon={"https://appcdn.goqii.com/storeimg/98410_1764053089.png"}
          title={t("ageGen.title")}
          
          subtitle={""}
         
        />
        </div>

        <div className="agegen-table">
          <div className="agegen-header-row">
            <div className="agegen-col agegen-col-age" />
            <div className="agegen-col agegen-col-label">
              <span className="agegen-pill agegen-pill-desktop">
                {t("ageGen.withoutGoqii")}
              </span>
            </div>
            <div className="agegen-col agegen-col-label">
              <span className="agegen-pill agegen-pill-desktop">
                {t("ageGen.withGoqii")}
              </span>
            </div>
          </div>

          {rows.map((row, idx) => (
            <React.Fragment key={row.age}>
              {idx > 0 && <div className="agegen-divider" />}

              <div className="agegen-row">
                <div className="agegen-col agegen-col-age">
                  <div className="agegen-age-text">
                    <div className="agegen-age-main">{`${t("ageGen.age")} ${row.age}`}</div>
                    <div className="agegen-age-sub">{t("ageGen.year")} {row.year}</div>
                  </div>
                </div>

                <div className="agegen-col agegen-col-image">
                  <div className="agegen-pill agegen-pill-mobile">{t("ageGen.withoutGoqii")}</div>
                  {row.withoutImg ? (
                    <img
                      src={row.withoutImg}
                      alt={`Age ${row.age} without GOQii`}
                      className="agegen-face-img"
                    />
                  ) : (
                    <div className="agegen-face-placeholder" />
                  )}
                </div>

                <div className="agegen-col agegen-col-image">
                  <div className="agegen-pill agegen-pill-mobile">{t("ageGen.withGoqii")}</div>
                  {row.withImg ? (
                    <img
                      src={row.withImg}
                      alt={`Age ${row.age} with GOQii`}
                      className="agegen-face-img"
                    />
                  ) : (
                    <div className="agegen-face-placeholder" />
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgeGen;
